import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechaFormatosGeneralesInterface } from "../../../../../interfaces/FechaFormatosGeneralesInterface";
import { EnvioResultadosInterface } from "../../../../../interfaces/EnvioResultadosInterface";
import HeaderPrint from "../../../../../components/HeaderFormsPrint/HeaderFormsPrint";
import fotoMapa from "../../../../../img/fotoMapa.png";

// Estilos CSS para impresión
const printStyles = `
  @media print {
    .page {
      position: relative;
      page-break-after: always;
      min-height: 100vh;
    }
    
    .footer-container {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
    }
    
    .firma-container {
      position: fixed;
      bottom: 60px;
      left: 0;
      right: 0;
      width: 100%;
      text-align: center;
    }
    
    /* Asegurarse que el contenido no se superponga con el footer */
    .content-container {
      padding-bottom: 200px;
    }
  }
`;

export const EnvioResultadosListaPrint = () => {
  const tituloFormulario = "ENVÍO DE RESULTADOS";
  const [formData, setFormData] = useState({
    day: "",
    month: "",
    year: "",
  });

  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, handleSubmit, setValue, reset } = useForm();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const queryFechaFG = useGetData<FechaFormatosGeneralesInterface>({
    url: `/fecha_formatosgenerales/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "fecha_formatosgenerales"],
  });

  const queryEnvioResultados = useGetData<EnvioResultadosInterface[]>({
    url: `/envio-resultados/${evaluadosList_id}`,
    name: ["evaluado", `${evaluadosList_id}`, "envio-resultados"],
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (queryFechaFG.data) {
      const fechafg = queryFechaFG.data;
      reset({
        fecha_requisitos_pruebas: fechafg.fecha_requisitos_pruebas || "",
      });
    }
  }, [queryFechaFG.data, reset]);

  useEffect(() => {
    if (queryEnvioResultados.data && queryEnvioResultados.data.length > 0) {
      const ER = queryEnvioResultados.data[0];
      reset({
        folio_er: ER.folio_er || "",
        nombre_er: ER.nombre_er || "",
        cargo_er: ER.cargo_er || "",
        empresa_er: ER.empresa_er || "",
        descripcion_er1: ER.descripcion_er1 || "",
        descripcion_er2: ER.descripcion_er2 || "",
        descripcion_er3: ER.descripcion_er3 || "",
        descripcion_er4: ER.descripcion_er4 || "",
        evaluado_uuid: ER.evaluado_uuid || "",
      });
    }
  }, [queryEnvioResultados.data, reset]);

  // Agregar estilos de impresión cuando el componente se monta
  useEffect(() => {
    // Crear elemento de estilo para reglas de impresión
    const styleElement = document.createElement("style");
    styleElement.type = "text/css";
    styleElement.appendChild(document.createTextNode(printStyles));
    document.head.appendChild(styleElement);

    // Limpiar el estilo cuando el componente se desmonte
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg overflow-hidden mt-4 mb-4 relative page">
      {/* Header */}
      <HeaderPrint titulo={tituloFormulario} />

      {/* Título del formulario */}
      <div id="headerContent" className="text-center">
        <h1
          className="text-black text-xl font-bold py-3 px-8 rounded-b-md inline-block flex justify-center transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
          style={{
            backgroundImage: `url(${fotoMapa})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {tituloFormulario}
        </h1>
      </div>

      {/* Contenido del formulario */}
      <div className="p-6 content-container">
        <div className="text-left mb-8">
          <h2 className="text-xl font-bold mb-2 flex items-center justify-end gap-2">
            <span>
              FOLIO:{" "}
              {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                ? queryEnvioResultados.data[0]?.folio_er || ""
                : ""}
            </span>
          </h2>
          <h2 className="text-xl font-bold mb-2 flex items-center justify-end gap-2">
            <span>
              FECHA:{" "}
              {queryFechaFG.data?.fecha_envio_res
                ? (() => {
                    const date = new Date(queryFechaFG.data.fecha_envio_res);
                    date.setDate(date.getDate() + 1);
                    return date.toLocaleDateString("es-ES", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    });
                  })()
                : "Fecha no disponible"}
            </span>
          </h2>
          <h2 className="text-xl font-bold mb-2 flex items-left">
            {`NOMBRE: ${
              queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                ? queryEnvioResultados.data[0]?.nombre_er || ""
                : ""
            }`}
          </h2>
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            <span>
              CARGO:{" "}
              {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                ? queryEnvioResultados.data[0]?.cargo_er || ""
                : ""}
            </span>
          </h2>
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            <span>
              EMPRESA:{" "}
              {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                ? queryEnvioResultados.data[0]?.empresa_er || ""
                : ""}
            </span>
          </h2>
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            <span>PRESENTE</span>
          </h2>
        </div>

        <div className="space-y-6">
          <p className="text-justify text-gray-800 leading-relaxed">
            <span className="mr-2">
              {queryEnvioResultados.data && queryEnvioResultados.data.length > 0
                ? queryEnvioResultados.data[0]?.descripcion_er1 || ""
                : ""}
            </span>
          </p>

          <p className="text-justify">
            No omito comentarle que resulta de suma importancia manejar los
            resultados con las debidas medidas de seguridad, para salvaguardar
            su confidencialidad y la de los evaluados.
          </p>

          <p className="text-justify">
            Cabe señalar que los resultados emitidos, emanan del apoyo que
            presta esta empresa en cumplimiento a la normatividad aplicable a la
            misma, ello en el respectivo ámbito de nuestras competencias y
            facultades, razón por la cual los resultados de las evaluaciones que
            solicita no serán vinculatorios a las determinaciones que emitan al
            respecto. Siendo estos únicamente de carácter informativo donde SKA
            TRACKING AND SECURITY S.A. de C.V. solo realiza conclusiones y
            recomendaciones a consideración y valoración estrictamente de la
            empresa contratante.
          </p>

          <p className="text-justify">
            En espera de que esta información contribuya a fortalecer el
            cumplimiento de las acciones encomendadas, me reitero a sus órdenes
            y aprovecho la ocasión para enviarle un cordial saludo.
          </p>
        </div>
      </div>

      {/* Firma */}
      <div className="firma-container">
        <div className="flex flex-col items-center w-full">
          <span className="font-medium text-center text-sm">ATENTAMENTE</span>
          <span className="font-bold text-center text-sm">
            GONZALO FRANCISCO CARRILLO NAVARRETE
          </span>
          <span className="text-center text-sm">
            EN REPRESENTACIÓN LEGAL DE
          </span>
          <span className="text-center font-semibold text-sm">
            SKA TRACKING AND SECURITY S.A. DE C.V.
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="footer-container">
        <div className="bg-customBlue/90 py-2 rounded-b-lg w-full">
          <p className="text-white text-xs text-center font-light px-4">
            <span className="font-medium">DOCUMENTO CONFIDENCIAL</span> •
            Prohibida su reproducción o copia fuera de las instalaciones de
            Skyangel.
          </p>
        </div>
      </div>
    </div>
  );
};
